import React, { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaGraduationCap, FaCertificate, FaAward,
  FaLeaf, FaSpa, FaBrain, FaSun,
  FaHeartbeat, FaChild, FaUsers, FaChalkboardTeacher, FaOm,
  FaUserTie, FaGlobeAsia, FaQuestionCircle, FaChevronDown
} from "react-icons/fa";
import {
  FiClock, FiBookOpen, FiCheckCircle, FiStar, FiAward, FiX,
  FiArrowRight, FiCompass, FiShield, FiUsers, FiCheck
} from "react-icons/fi";
import { GiLotus, GiMeditation, GiSparkles } from "react-icons/gi";

// ─── AUTH HELPER ─────────────────────────────────────────────────────────────
const getAuthState = () => {
  try {
    const token = localStorage.getItem("divyayoga_token");
    const role = localStorage.getItem("divyayoga_role");
    const user = JSON.parse(localStorage.getItem("divyayoga_user") || "null");
    if (token && user) return { loggedIn: true, role, user };
  } catch {}
  return { loggedIn: false, role: null, user: null };
};

// ─── 3D LUXURY TILT CARD COMPONENT ─────────────────────────────────────────────
function LuxuryTiltCard({ children, className = "", maxTilt = 8, style = {} }) {
  const ref = useRef(null);
  const rotateX = useSpring(useMotionValue(0), { stiffness: 220, damping: 22 });
  const rotateY = useSpring(useMotionValue(0), { stiffness: 220, damping: 22 });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rotateX.set(py * -maxTilt);
    rotateY.set(px * maxTilt);
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        transformPerspective: 1200,
        ...style,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── PEDAGOGICAL CURRICULUM DATA (WHAT STUDENTS LEARN FROM MASTER TRAINERS) ──
const COURSES_DATA = [
  {
    id: "beginner-yoga",
    title: "Foundations of Classical Hatha",
    subtitle: "Sacred Postures, Alignment & Yogic Breathing",
    instructor: "Mrs. Divya Nithin Bhojwani",
    instructorRole: "Founder & International Gold Medalist",
    instructorImg: "/assets/Divya_1.png",
    duration: "4 Weeks",
    level: "Beginner",
    category: "beginner",
    rating: "4.98",
    learners: "3,400+ Practitioners",
    image: "/assets/course_hatha_yoga.jpg",
    tag: "Core Foundation",
    icon: FaLeaf,
    overview: "This foundational curriculum deconstructs the essential geometry of classical yoga postures, breath awareness, spinal health, and meditative grounding.",
    whatYouWillLearn: [
      "Mastery of 24 Classical Asanas with precise anatomical alignment",
      "Correct pelvic, knee, and shoulder placement to avoid injury",
      "Pranayama breathing mechanics: Nadi Shodhana (Alternate Nostril) & Ujjayi",
      "Foundations of Surya Namaskar (Sun Salutations) with Vedic mantras",
      "Spinal de-compression techniques for desk workers and beginners",
      "Guided Yoga Nidra relaxation for restorative nervous system calm"
    ],
    modules: [
      { title: "Module 1: Joint Articulation & Spinal Geometry", desc: "Sukshma Vyayama, unlocking stiff hips, shoulders, and correcting standing posture." },
      { title: "Module 2: Foundational Standing & Balancing Asanas", desc: "Tadasana, Vrikshasana, Trikonasana, and grounding root-lock awareness." },
      { title: "Module 3: Breath-Movement Rhythms (Pranayama)", desc: "Synchronizing deep diaphragmatic breathing with 12-count Sun Salutations." },
      { title: "Module 4: Seated Twists, Restorations & Meditation", desc: "Paschimottanasana, Ardha Matsyendrasana, and building a daily home sadhana." }
    ],
    targetBenefits: "Eliminates morning stiffness, corrects slouching posture, relieves lower back strain, and builds peaceful mental focus."
  },
  {
    id: "intermediate-yoga",
    title: "Vinyasa Flow & Dynamic Power",
    subtitle: "Fluid Transitions, Core Awakening & Arm Balances",
    instructor: "Hithesh Gowda C A",
    instructorRole: "Hatha & Dynamic Vinyasa Specialist",
    instructorImg: "/assets/Hitesh.png",
    duration: "6 Weeks",
    level: "Intermediate",
    category: "intermediate",
    rating: "4.99",
    learners: "2,850+ Practitioners",
    image: "/assets/image 33.png",
    tag: "Dynamic Flow",
    icon: FaSpa,
    overview: "Cultivate seamless breath-linked transitions, internal core heat (Agni), arm balances, and cardiovascular stamina through dynamic sequencing.",
    whatYouWillLearn: [
      "Smooth Ashtanga-style jump-backs, jump-throughs, and floating transitions",
      "Activating Mula Bandha (Root Lock) and Uddiyana Bandha for lightness",
      "Arm balance progressions: Bakasana (Crow Pose) and Parsva Bakasana",
      "Safe thoracic spine backbending: Urdhva Dhanurasana (Wheel Pose)",
      "Dynamic hip openers: Eka Pada Rajakapotasana prep and Hanumanasana drills",
      "Bhastrika (Bellows breath) for heightened metabolic stamina"
    ],
    modules: [
      { title: "Module 1: Seamless Vinyasa Sequencing", desc: "Linking breath with rapid transitions and strengthening wrists & shoulders." },
      { title: "Module 2: Core Ignition & Weightless Inversions", desc: "Dolphin pose, headstand entries, and cultivating abdominal buoyancy." },
      { title: "Module 3: Advanced Hip & Hamstring Agility", desc: "Deep lunges, warrior sequences, and preparing for sovereign arm balances." },
      { title: "Module 4: Peak Flows & Sustained Endurance", desc: "Full 60-minute continuous flow followed by cooling Pranayama." }
    ],
    targetBenefits: "Increases muscular definition, athletic agility, deep hip flexibility, and high physical endurance."
  },
  {
    id: "advanced-yoga",
    title: "Advanced Asana & Inversion Mastery",
    subtitle: "Sirsasana, Pincha Mayurasana & Deep Spinal Sadhana",
    instructor: "Mrs. Divya Nithin Bhojwani",
    instructorRole: "Founder & International Gold Medalist",
    instructorImg: "/assets/Divya_1.png",
    duration: "8 Weeks",
    level: "Advanced",
    category: "advanced",
    rating: "5.0",
    learners: "1,600+ Practitioners",
    image: "/assets/image 34.jpeg",
    tag: "Mastery Level",
    icon: FaBrain,
    overview: "A masterclass curriculum exploring peak inversions, forearm balances, dropbacks, and deep pranic retention for advanced physical mastery.",
    whatYouWillLearn: [
      "Freestanding Sirsasana (Headstand) with lotus and leg variations",
      "Pincha Mayurasana (Forearm Balance) and Handstand press drills",
      "Advanced backbends: Dropbacks from standing to Wheel & Kapotasana",
      "Kumbhaka (Breath retention) techniques for prana management",
      "Overcoming fear barriers in inversions through biomechanical stability",
      "Deep Chakra meditation and Dharana (unbroken concentration)"
    ],
    modules: [
      { title: "Module 1: Inversion Biomechanics & Scapular Elevation", desc: "Building fortress shoulder strength and wall-free balance control." },
      { title: "Module 2: King Asanas & Spinal Extensions", desc: "Rajakapotasana, Natarajasana (Dancer), and thoracic spine expansion." },
      { title: "Module 3: Complex Arm Balances & Twists", desc: "Eka Pada Koundinyasana, Astavakrasana, and floating transitions." },
      { title: "Module 4: Pranic Integration & Silent Sadhana", desc: "Mahamudra, advanced Bandhas, and Raja Yoga meditative absorption." }
    ],
    targetBenefits: "Mastery over inversions, profound mental fearlessness, extraordinary spine elasticity, and internal poise."
  },
  {
    id: "morning-wellness",
    title: "Sunrise Sadhana & Daily Vitality",
    subtitle: "Energizing Morning Flows, Sun Salutations & Solar Breathwork",
    instructor: "Sapanpreet Grewal",
    instructorRole: "Hatha Yog & Mindfulness Guide (Yoga Siromani)",
    instructorImg: "/assets/Sapanpreet_Grewal.png",
    duration: "Daily / Ongoing",
    level: "All Levels",
    category: "wellness",
    rating: "4.97",
    learners: "5,200+ Practitioners",
    image: "/assets/image 35.jpeg",
    tag: "Daily Practice",
    icon: FaSun,
    overview: "Awaken each morning with traditional Vedic chanting, cleansing kriyas, and full-body vitality flows to center your day in sacred stillness.",
    whatYouWillLearn: [
      "Traditional 12-step Surya Namaskar with solar bija mantras",
      "Kapalabhati (Skull-shining breath) to eliminate lethargy and toxins",
      "Surya Bhedana (Right nostril activation) for daytime mental alertness",
      "Gentle full-body awakening sequence to release sleep stiffness",
      "Guided intention setting (Sankalpa) and mindfulness journaling",
      "Cultivating emotional equilibrium and stress immunity for busy days"
    ],
    modules: [
      { title: "Routine 1: Sacred Dawn Invocation & Joint Awakening", desc: "Chanting Gayatri & Mahamrityunjaya mantras with gentle joint warmups." },
      { title: "Routine 2: 12-Round Surya Namaskar Flow", desc: "Rhythmic cardiovascular synchronization and metabolic activation." },
      { title: "Routine 3: Solar Pranayama & Mindful Stillness", desc: "Energizing breathwork followed by 10 minutes of silent meditation." }
    ],
    targetBenefits: "Eliminates morning fatigue, boosts natural immunity, sharpens mental productivity, and fosters inner gratitude."
  },
  {
    id: "weight-management",
    title: "Metabolic Yoga & Core Sculpt",
    subtitle: "Therapeutic Asanas for Digestion, Agni & Weight Optimization",
    instructor: "Hithesh Gowda C A",
    instructorRole: "Hatha & Dynamic Vinyasa Specialist",
    instructorImg: "/assets/Hitesh.png",
    duration: "6 Weeks",
    level: "Beginner / Inter",
    category: "wellness",
    rating: "4.96",
    learners: "3,800+ Practitioners",
    image: "/assets/course_metabolic_yoga.jpg",
    tag: "Body Toning",
    icon: FaHeartbeat,
    overview: "Ignite your internal digestive fire (Jatharagni) through targeted visceral twisting asanas, core stability holds, and Sattvic lifestyle principles.",
    whatYouWillLearn: [
      "Dynamic twisting sequences: Parivrtta Trikonasana and Utkatasana twists",
      "Deep core engagement: Navasana (Boat Pose) variations and plank sequences",
      "Agnisar Kriya for abdominal visceral stimulation and digestion",
      "Thyroid-stimulating inversions: Sarvangasana (Shoulderstand) and Halasana",
      "High-tempo Surya Namaskars for natural cardiovascular fat burning",
      "Sattvic lifestyle guidelines to overcome emotional eating and cravings"
    ],
    modules: [
      { title: "Module 1: Igniting the Core & Digestive Fire", desc: "Abdominal toning, core alignment, and digestive cleansing kriyas." },
      { title: "Module 2: Lower Body & Glute Strength Flows", desc: "Warrior pulses, balancing poses, and lymphatic drainage postures." },
      { title: "Module 3: Metabolic Endurance & Hormonal Alignment", desc: "Thyroid-stimulating inverted flows and stamina intervals." }
    ],
    targetBenefits: "Accelerates natural metabolism, strengthens abdominal wall, promotes healthy digestion, and tones muscles."
  },
  {
    id: "stress-relief",
    title: "Restorative Yoga & Yoga Nidra",
    subtitle: "Nervous System Healing, Insomnia Relief & Deep Stillness",
    instructor: "Sapanpreet Grewal",
    instructorRole: "Hatha Yog & Mindfulness Guide (Yoga Siromani)",
    instructorImg: "/assets/Sapanpreet_Grewal.png",
    duration: "4 Weeks",
    level: "All Levels",
    category: "therapy",
    rating: "4.99",
    learners: "4,500+ Practitioners",
    image: "/assets/course_restorative_yoga.jpg",
    tag: "Deep Relaxation",
    icon: FaSpa,
    overview: "Dissolve chronic mental anxiety, burnout, and insomnia through prop-supported restorative asanas, soothing sound frequencies, and authentic Yoga Nidra.",
    whatYouWillLearn: [
      "Supported restorative postures: Supta Baddha Konasana and Supported Bridge",
      "Vagus nerve stimulation techniques for instant parasympathetic relaxation",
      "Chandra Bhedana (Left nostril cooling breath) and Bhramari (Bee breath)",
      "45-minute guided Yoga Nidra psychic sleep journeys for subconscious healing",
      "Releasing stored emotional trauma and muscular gripping from hips & neck",
      "Bedtime wind-down rituals for restorative, uninterrupted deep sleep"
    ],
    modules: [
      { title: "Module 1: Physical Decompression & Fascial Release", desc: "Releasing chronic neck, shoulder, and spinal tension using bolsters." },
      { title: "Module 2: Calming the Emotional Heart Center", desc: "Gentle heart opening, passive chest expansion, and cooling breathwork." },
      { title: "Module 3: Deep Subconscious Healing (Yoga Nidra)", desc: "Systematic body scans, breath visualization, and neural restoration." }
    ],
    targetBenefits: "Cures sleeplessness, reduces cortisol, calms racing thoughts, and restores profound emotional peace."
  },
  {
    id: "prenatal-postnatal",
    title: "Sacred Motherhood & Women's Health",
    subtitle: "Face Yoga, Trimester Posture Care & Hormonal Balance",
    instructor: "Varsha Rochwani",
    instructorRole: "Face Yoga & Women's Wellness Specialist",
    instructorImg: "/assets/Varsha_Rochwani.png",
    duration: "6 Weeks",
    level: "Specialized",
    category: "therapy",
    rating: "5.0",
    learners: "1,200+ Practitioners",
    image: "/assets/image 38.jpeg",
    tag: "Women's Wellness",
    icon: FaChild,
    overview: "Specialized holistic care focusing on natural facial rejuvenation, pelvic floor health, hormonal equilibrium, and gentle prenatal guidance.",
    whatYouWillLearn: [
      "Natural Face Yoga techniques for facial muscle tone, lymphatic glow, and anti-aging",
      "Pelvic floor strengthening and relaxation exercises for women of all ages",
      "Therapeutic yoga for PCOD, thyroid balance, and menstrual comfort",
      "Trimester-safe posture adaptations and breath awareness for expecting mothers",
      "Gentle joint decompression and lower back relief from pregnancy posture changes",
      "Restorative postpartum recovery exercises for abdominal recovery"
    ],
    modules: [
      { title: "Module 1: Face Yoga Sculpt & Lymphatic Drainage", desc: "Facial acupressure points, jawline sculpting, and radiant skin stimulation." },
      { title: "Module 2: Hormonal Harmony & Pelvic Health", desc: "Gentle butterfly flows, restorative inversions, and soothing breathwork." },
      { title: "Module 3: Safe Prenatal & Postnatal Nurturing", desc: "Supported squats, birth ball stretches, and deep maternal relaxation." }
    ],
    targetBenefits: "Naturally toned facial muscles, balanced hormonal cycles, relief from menstrual pain, and safe maternal support."
  },
  {
    id: "kids-yoga",
    title: "Kids Mindful Movement & Memory",
    subtitle: "Playful Postures, Concentration Games & Emotional Poise",
    instructor: "Hithesh Gowda C A",
    instructorRole: "Hatha & Dynamic Vinyasa Specialist",
    instructorImg: "/assets/Hitesh.png",
    duration: "4 Weeks",
    level: "Kids (6-14 Yrs)",
    category: "specialized",
    rating: "4.95",
    learners: "1,900+ Practitioners",
    image: "/assets/image 39.jpeg",
    tag: "Kids & Teens",
    icon: FaChild,
    overview: "An engaging, joyful program designed to build natural flexibility, sharp academic focus, healthy posture, and emotional resilience in children.",
    whatYouWillLearn: [
      "Playful animal asanas: Simhasana (Lion breath), Bhujangasana, and Garudasana",
      "Trataka concentration drills and memory-sharpening exercises",
      "Correcting screen-time neck slouching and spinal rounding",
      "Calming hyperactive energy through rhythmic breathing techniques",
      "Building self-confidence, bodily awareness, and positive self-talk",
      "Fun partner yoga games encouraging compassion and teamwork"
    ],
    modules: [
      { title: "Module 1: The Jungle Kingdom of Yoga", desc: "Story-based posture games exploring animal movements and joint agility." },
      { title: "Module 2: Laser Focus & Mind Gym", desc: "Tree balance challenges, breath counting, and memory enhancement." },
      { title: "Module 3: Courage, Posture & Stillness", desc: "Warrior sequences, posture correction, and calming bedtime breathing." }
    ],
    targetBenefits: "Corrects child posture, enhances exam concentration, curbs screen addiction, and promotes sound sleep."
  },
  {
    id: "senior-wellness",
    title: "Senior Longevity & Chair Yoga",
    subtitle: "Joint Mobility, Balance Restoration & Gentle Wellness",
    instructor: "Varsha Rochwani",
    instructorRole: "Face Yoga & Chair Yoga Specialist",
    instructorImg: "/assets/Varsha_Rochwani.png",
    duration: "Ongoing",
    level: "Gentle / Seniors",
    category: "therapy",
    rating: "4.99",
    learners: "2,600+ Practitioners",
    image: "/assets/image 40.jpeg",
    tag: "Gentle Living",
    icon: FaUsers,
    overview: "Gentle chair-assisted and wall-supported routines designed to lubricate aging joints, improve balance, and maintain youthful vitality.",
    whatYouWillLearn: [
      "100% seated chair yoga postures requiring zero floor sitting",
      "Sandhi Chalana (Gentle joint rotation) for arthritis and stiff knees",
      "Wall-supported balance drills to prevent slip-and-fall incidents",
      "Gentle spinal twisting to improve digestion and bowel regularity",
      "Mild therapeutic breathing to maintain healthy blood pressure levels",
      "Meditation and gratitude sadhana for mental contentment and peace"
    ],
    modules: [
      { title: "Module 1: Chair-Based Joint Articulation", desc: "Restoring neck, wrist, shoulder, knee, and ankle mobility from a chair." },
      { title: "Module 2: Supported Balance & Core Stability", desc: "Chair-assisted Warrior, Tree pose, and gentle pelvic tilting." },
      { title: "Module 3: Gentle Longevity Pranayama", desc: "Soothing Anulom Vilom, Bhramari, and restorative resting." }
    ],
    targetBenefits: "Restores easy mobility, relieves chronic knee/hip pain, boosts stability, and supports healthy blood pressure."
  },
  {
    id: "teacher-training",
    title: "200-Hour Master Teacher Certification (TTC)",
    subtitle: "Yoga Alliance USA Registered Pedagogy & Anatomy Immersion",
    instructor: "Mrs. Divya Nithin Bhojwani",
    instructorRole: "Founder & International Gold Medalist",
    instructorImg: "/assets/Divya_1.png",
    duration: "30 Days Immersion",
    level: "Mastery",
    category: "certification",
    rating: "5.0",
    learners: "850+ Certified Teachers",
    image: "/assets/image 41.jpeg",
    tag: "Global Credential",
    icon: FaChalkboardTeacher,
    overview: "The international gold standard in yogic pedagogy. Master Patanjali Yoga Sutras, functional musculoskeletal anatomy, hands-on adjustments, and teaching methodology.",
    whatYouWillLearn: [
      "In-depth biomechanical breakdown of 84 classical Asanas and safe variations",
      "Hands-on physical adjustments, alignment cues, and voice modulation",
      "Functional anatomy, fascia, musculoskeletal system, and injury prevention",
      "Vedic philosophy: Patanjali Yoga Sutras, Bhagavad Gita, and Hatha Pradipika",
      "Structuring safe, transformative class sequences for different body types",
      "Yoga Alliance USA international credentials and yoga studio business ethics"
    ],
    modules: [
      { title: "Module 1: Asana Biomechanics & Precise Cueing", desc: "Deconstructing posture geometry, contraindications, and student modifications." },
      { title: "Module 2: Human Anatomy & Energetic Physiology", desc: "Nervous system, respiratory mechanics, Chakras, Nadis, and Koshas." },
      { title: "Module 3: Scriptural Wisdom & Teaching Ethics", desc: "Patanjali eight limbs of yoga, Sanskrit terms, and authentic acharya conduct." },
      { title: "Module 4: Live Teaching Practicum & Certification", desc: "Student teaching labs, feedback from Divya Nithin Bhojwani, and graduation." }
    ],
    targetBenefits: "Yoga Alliance USA RYT-200 international certification, professional teaching mastery, and deep sadhana foundation."
  }
];

const CATEGORIES = [
  { id: "all", label: "All Curriculums", count: 10 },
  { id: "beginner", label: "Beginner Foundations", count: 2 },
  { id: "intermediate", label: "Intermediate Flow", count: 1 },
  { id: "advanced", label: "Advanced Mastery", count: 1 },
  { id: "wellness", label: "Daily Wellness", count: 2 },
  { id: "therapy", label: "Therapy & Healing", count: 3 },
  { id: "certification", label: "Teacher Certification", count: 1 },
];

const COURSE_FAQS = [
  {
    question: "Which curriculum is best suited if I am a complete beginner or experiencing stiffness?",
    answer: "Our 'Foundations of Classical Hatha' (led by Mrs. Divya Nithin Bhojwani) and 'Sunrise Sadhana & Daily Vitality' are specifically engineered for beginners. Every posture begins with anatomical joint loosening (Sukshma Vyayama) and prop-supported modifications to build flexibility safely without strain."
  },
  {
    question: "Do instructors give live corrections during the interactive classes?",
    answer: "Yes! All Divyyog live sessions feature two-way interactive instruction. Master trainers observe student cameras in real-time, providing immediate verbal alignment corrections for spinal safety, joint protection, and deeper energetic benefit."
  },
  {
    question: "Can I attend classes if my work or daily schedule changes?",
    answer: "Absolutely. You can seamlessly switch between morning, evening, and weekend batches anytime directly from your dashboard. Additionally, high-definition session replays and masterclasses are accessible 24/7 in your Training Library."
  },
  {
    question: "Are certificates provided upon completing teacher training or specialized courses?",
    answer: "Yes. Upon finishing certified curriculums like the 200-Hour Master Teacher Certification (TTC), students complete a practical evaluation and receive Yoga Alliance USA recognized digital certificates signed by International Gold Medalist Divya Nithin Bhojwani."
  },
  {
    question: "How do I book a one-on-one consultation with a master trainer?",
    answer: "You can request a personalized consultation with our faculty desk to assess your flexibility, spinal health, and wellness objectives. Our trainers will map a tailored roadmap suited to your body's specific needs."
  }
];

export default function Courses() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCourseModal, setSelectedCourseModal] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [auth, setAuth] = useState(getAuthState);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  useEffect(() => {
    setAuth(getAuthState());
  }, []);

  const filteredCourses = useMemo(() => {
    if (selectedCategory === "all") return COURSES_DATA;
    return COURSES_DATA.filter((course) => course.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <div className="relative overflow-hidden bg-[#FFF9F2] min-h-screen text-[#111111]">
      
      {/* ══════════════════════════════════════════════════════════════════════════
          1. LUXURY HERO BANNER (OLIVE GREEN, SAFFRON ORANGE & WARM BROWN THEME)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[90vh] lg:min-h-screen flex items-center justify-center overflow-hidden">
        
        {/* Background Photorealistic Ashram Artwork */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-[1.02] transition-transform duration-1000"
          style={{
            backgroundImage: "url('/assets/courses_hero_bg.jpg')",
          }}
        />

        {/* ── Balanced Subtle Vignette: Preserves Rich Image Visibility with High Contrast ── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(180deg, rgba(12, 6, 2, 0.50) 0%, rgba(12, 6, 2, 0.30) 40%, rgba(15, 7, 2, 0.60) 100%)",
          }}
        />
        {/* Soft bottom edge transition (transparent to subtle 15% blend) */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFF9F2]/15 via-transparent to-black/25 pointer-events-none" />

        {/* Subtle Ambient Glowing Aura Orbs (Low Opacity, No Harsh Color Blobs) */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.12, 0.22, 0.12] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-[450px] h-[450px] rounded-full bg-[#7D9D64]/25 blur-[120px] pointer-events-none"
        />
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.10, 0.20, 0.10] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
          className="absolute bottom-1/3 right-1/4 w-[450px] h-[450px] rounded-full bg-[#F29A0C]/20 blur-[120px] pointer-events-none"
        />

        {/* Floating Sacred Aura Stardust Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(26)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -45, 0],
                x: [0, ((i * 7) % 30) - 15, 0],
                opacity: [0.25, 0.9, 0.25],
                scale: [1, 1.4, 1],
              }}
              transition={{
                duration: 5 + (i % 5),
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.22,
              }}
              className="absolute rounded-full"
              style={{
                width: `${3 + (i % 4) * 2}px`,
                height: `${3 + (i % 4) * 2}px`,
                backgroundColor: i % 3 === 0 ? "#9DC08B" : i % 3 === 1 ? "#FFD166" : "#FFA41B",
                boxShadow: "0 0 12px rgba(242,154,12,0.8)",
                top: `${8 + (i * 7.5) % 80}%`,
                left: `${4 + (i * 8.2) % 92}%`,
              }}
            />
          ))}
        </div>

        {/* Hero Central Header */}
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center pt-28 pb-20">
          
          {/* Top Royal Seal Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 px-6 py-2.5 rounded-full bg-black/60 border border-[#F29A0C]/50 text-[#FFD166] text-xs font-bold uppercase tracking-[0.25em] shadow-[0_10px_30px_rgba(0,0,0,0.4)] backdrop-blur-xl mb-8"
          >
            <GiLotus className="text-base text-[#F29A0C] animate-pulse" />
            <span>Sacred Himalayan Curriculum & Pedagogy Guide</span>
          </motion.div>

          {/* Main Royal Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.15 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.2] sm:leading-[1.15] tracking-[0.02em] sm:tracking-[0.025em] drop-shadow-[0_6px_25px_rgba(0,0,0,0.9)] max-w-5xl mx-auto"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            What You Learn with Our <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-[#FFD166] via-[#FFA41B] to-[#F29A0C] bg-clip-text text-transparent drop-shadow-[0_4px_30px_rgba(242,154,12,0.7)]">
              Living Yoga Masters
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.3 }}
            className="text-white/95 text-base sm:text-xl font-medium leading-[1.7] tracking-[0.015em] mt-8 max-w-3xl mx-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]"
          >
            Explore our specialized yogic curriculums deconstructing precise asana geometry, therapeutic breathwork, and meditative sadhana taught directly by certified master faculty.
          </motion.p>

          {/* Quick Pillars Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.45 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto"
          >
            {[
              { label: "Faculty Instruction", val: "Direct Acharya Mentorship", icon: FaUserTie },
              { label: "Anatomical Safety", val: "Biomechanical Precision", icon: FiShield },
              { label: "Classical Lineage", val: "Pure Vedic Pedagogy", icon: GiLotus },
              { label: "Learning Roadmaps", val: "Structured Syllabuses", icon: FiBookOpen },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div
                  key={i}
                  className="p-4 rounded-2xl bg-black/60 border border-[#F4D9B5]/30 backdrop-blur-xl text-center space-y-1 shadow-lg"
                >
                  <Icon className="mx-auto text-lg text-[#FFD166]" />
                  <div className="text-white font-bold text-sm tracking-wide">{stat.val}</div>
                  <div className="text-white/70 text-[11px] font-medium">{stat.label}</div>
                </div>
              );
            })}
          </motion.div>

        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          2. CATEGORY FILTERS & SEARCH CONTROLS
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="relative z-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 -mt-8">
        
        <div className="p-6 sm:p-8 rounded-[32px] bg-white/95 border-2 border-[#F4D9B5] shadow-[0_20px_60px_rgba(242,154,12,0.1)] backdrop-blur-2xl space-y-6">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#F4D9B5]/60 pb-5">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] leading-tight tracking-[0.02em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Select Your Learning Pathway
              </h2>
              <p className="text-xs sm:text-sm text-[#666666] font-medium mt-1">
                Explore what yoga techniques, pranayamas, and sadhanas you will master in each curriculum.
              </p>
            </div>

            <div className="text-xs font-bold text-[#F29A0C] bg-[#FFF8EE] px-4 py-2 rounded-full border border-[#F4D9B5] self-start md:self-auto">
              Showing {filteredCourses.length} of {COURSES_DATA.length} Available Curriculums
            </div>
          </div>

          {/* Filter Pills (mobile: single swipeable row via ud-scroll) */}
          <div className="ud-scroll ud-scroll-pad flex flex-wrap gap-2.5">
            {CATEGORIES.map((cat) => {
              const active = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-5 py-3 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 cursor-pointer flex items-center gap-2.5 ${
                    active
                      ? "bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white shadow-[0_8px_25px_rgba(242,154,12,0.35)] scale-105"
                      : "bg-[#FFF8EE] text-[#444444] hover:text-[#111111] border border-[#F4D9B5] hover:bg-white"
                  }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-extrabold ${
                    active ? "bg-black/25 text-white" : "bg-white text-[#F29A0C] border border-[#F4D9B5]"
                  }`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>

        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          3. EDUCATIONAL COURSE CARDS (WHAT YOU LEARN FROM TRAINERS • ZERO PRICES)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-12 max-w-[1440px] mx-auto relative z-20">
        
        <AnimatePresence mode="popLayout">
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10"
          >
            {filteredCourses.map((course) => {
              const Icon = course.icon;
              return (
                <motion.div
                  key={course.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.45 }}
                  className="flex"
                >
                  <LuxuryTiltCard className="w-full flex">
                    <div className="w-full rounded-[36px] bg-white border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:shadow-[0_30px_80px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] transition-all duration-500 overflow-hidden flex flex-col justify-between p-6 sm:p-7 relative group">
                      
                      {/* Top Radiant Shimmer Line */}
                      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#F29A0C] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                      <div className="space-y-6">
                        
                        {/* ── LUXURY FULL-BLEED IMAGE CONTAINER (ELEGANT SEAMLESS COVER, NO EMPTY MARGINS) ── */}
                        <div className="relative rounded-[24px] overflow-hidden h-[240px] sm:h-[260px] w-full bg-[#201006] shadow-md group/img">
                          
                          {/* Elegant, fully covering high-resolution image */}
                          <img
                            src={course.image}
                            alt={course.title}
                            className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover/img:scale-108 select-none"
                            loading="lazy"
                          />

                          {/* Luxury dark gradient vignette for badges readability */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-black/25 pointer-events-none" />

                          {/* Top Left Icon Pill */}
                          <div className="absolute top-3.5 left-3.5 z-20 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-md border border-white/60 text-[#F29A0C] flex items-center justify-center text-lg shadow-md group-hover:bg-[#F29A0C] group-hover:text-white transition-all duration-300">
                            <Icon />
                          </div>

                          {/* Top Right Tag Badge */}
                          <div className="absolute top-3.5 right-3.5 z-20 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-[10.5px] font-extrabold uppercase tracking-wider shadow-md">
                            {course.tag}
                          </div>

                          {/* Bottom Badges Inside Image */}
                          <div className="absolute bottom-3 left-3.5 right-3.5 z-20 flex items-center justify-between text-xs">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-bold shadow-sm">
                              <FiClock className="text-[#FFD166]" />
                              <span>{course.duration}</span>
                            </div>
                            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-extrabold shadow-sm">
                              <FiStar className="text-[#FFD166] fill-current" />
                              <span>{course.rating}</span>
                            </div>
                          </div>

                        </div>

                        {/* ── COURSE HEADER & LEVEL BADGE ── */}
                        <div className="space-y-2.5">
                          
                          {/* Course Level & Learners Info */}
                          <div className="flex items-center justify-between gap-2 pt-0.5">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#6B8A5A] px-3 py-1 rounded-full bg-[#6B8A5A]/10 border border-[#6B8A5A]/20">
                              {course.level} Level
                            </span>
                            <span className="text-[11px] font-semibold text-[#777777] flex items-center gap-1.5">
                              <FiUsers className="text-[#F29A0C]" size={13} />
                              <span>{course.learners}</span>
                            </span>
                          </div>

                          <h3
                            onClick={() => setSelectedCourseModal(course)}
                            className="text-xl sm:text-2xl font-bold text-[#111111] leading-snug tracking-[0.02em] group-hover:text-[#F29A0C] transition-colors cursor-pointer"
                            style={{ fontFamily: "'Poppins', sans-serif" }}
                          >
                            {course.title}
                          </h3>
                          
                          <p className="text-xs sm:text-sm font-semibold text-[#F29A0C] leading-snug">
                            {course.subtitle}
                          </p>

                          <p className="text-xs text-[#666666] leading-[1.65] line-clamp-2 pt-0.5 font-normal">
                            {course.overview}
                          </p>
                        </div>

                        {/* ── WHAT YOU WILL LEARN FROM THE TRAINER ── */}
                        <div className="space-y-2 pt-2 border-t border-[#F4D9B5]/60">
                          <div className="text-[11px] font-extrabold uppercase tracking-wider text-[#6B8A5A] flex items-center gap-1.5">
                            <FiBookOpen size={12} />
                            <span>What You Will Learn:</span>
                          </div>
                          <div className="space-y-1.5">
                            {course.whatYouWillLearn.slice(0, 3).map((item, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-[#444444]">
                                <FiCheck className="text-[#6B8A5A] text-sm flex-shrink-0 mt-0.5 font-bold" />
                                <span className="line-clamp-1">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>

                      {/* ── CARD BOTTOM ACTIONS (NO PRICING • LEARNING OUTCOME ORIENTED) ── */}
                      <div className="pt-6 border-t border-[#F4D9B5]/80 flex items-center gap-3">
                        <button
                          onClick={() => setSelectedCourseModal(course)}
                          className="flex-1 py-3 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-xs font-bold text-[#111111] hover:bg-[#F4D9B5]/40 transition-colors cursor-pointer text-center flex items-center justify-center gap-1.5"
                        >
                          <FiBookOpen />
                          <span>View Syllabus</span>
                        </button>
                        
                        {auth.loggedIn ? (
                          <Link to="/user/dashboard" className="flex-1">
                            <motion.button
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              className="w-full py-3 rounded-full bg-gradient-to-r from-[#6B8A5A] to-[#4B6B3A] text-xs font-bold text-white shadow-md hover:shadow-lg transition-all cursor-pointer flex items-center justify-center gap-1.5"
                            >
                              <span>Open Dashboard</span>
                              <FiArrowRight />
                            </motion.button>
                          </Link>
                        ) : (
                          <Link to="/register" className="flex-1">
                            <motion.button
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              className="w-full py-3 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-xs font-bold text-white shadow-md hover:shadow-lg transition-all cursor-pointer flex items-center justify-center gap-1.5"
                            >
                              <span>Join Learning</span>
                              <FiArrowRight />
                            </motion.button>
                          </Link>
                        )}
                      </div>

                    </div>
                  </LuxuryTiltCard>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          4. THE 4-STAGE YOGIC SADHANA ROADMAP & SACRED CURRICULUM EVOLUTION
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-28 px-4 sm:px-6 lg:px-12 relative overflow-hidden bg-gradient-to-b from-[#FFF9F2] via-[#FFF3DF] to-[#FFF9F2]">
        
        {/* Layered Serene Ashram Background Artwork with Warm Glows */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <img
            src="/assets/image 25.jpeg"
            alt=""
            className="w-full h-full object-cover opacity-35 scale-105"
            loading="lazy"
          />
          {/* Radiant Spiritual Glow Orbs */}
          <div className="absolute top-[10%] left-[-100px] w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.22)_0%,transparent_70%)] blur-[80px]" />
          <div className="absolute top-[45%] right-[-120px] w-[650px] h-[650px] rounded-full bg-[radial-gradient(circle,rgba(107,138,90,0.20)_0%,transparent_70%)] blur-[90px]" />
          <div className="absolute bottom-[5%] left-[20%] w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(255,180,60,0.18)_0%,transparent_70%)] blur-[90px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFF9F2]/70 via-[#FFF3DF]/45 to-[#FFF9F2]/80" />
        </div>

        <div className="max-w-[1440px] mx-auto relative z-10 space-y-24">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-gradient-to-r from-[#26381C]/15 via-[#FFF8EE] to-[#F29A0C]/15 border border-[#F4D9B5] text-[#26381C] text-xs font-black uppercase tracking-[3px] shadow-[0_8px_25px_rgba(242,154,12,0.12)] backdrop-blur-xl"
            >
              <GiSparkles className="text-sm text-[#F29A0C] animate-pulse" />
              <span>SACRED PROGRESSION ROADMAP</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#111111] leading-[1.2] tracking-tight"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              The 4-Stage Yogic Transformation Journey
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-[#555555] text-sm sm:text-base font-medium leading-[1.7] max-w-2xl mx-auto"
            >
              Every Divyyog curriculum is scientifically architected to move you from fundamental biomechanical stability to deep energetic mastery.
            </motion.p>
          </div>

          {/* 4-Stage Interactive Roadmap Cards with Enhanced Gradients & Animations */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">
            {[
              {
                step: "Stage 01",
                badge: "Foundations",
                title: "Sthira & Sukham",
                subtitle: "Anatomical Stability",
                desc: "Correction of spinal alignment, joint de-compression, core neuromuscular stabilization, and learning safe personal modifications.",
                icon: FaLeaf,
                color: "#6B8A5A",
                grad: "from-[#6B8A5A] to-[#4B6B3A]",
                bgLight: "from-[#6B8A5A]/10 to-transparent",
                borderCol: "rgba(107,138,90,0.3)"
              },
              {
                step: "Stage 02",
                badge: "Breath & Flow",
                title: "Prana Vayu",
                subtitle: "Energy Synchronization",
                desc: "Unlocking lung capacity with 8 classical Pranayamas, fluid Vinyasa transitions, and activating the central nervous system reset.",
                icon: FaSun,
                color: "#F29A0C",
                grad: "from-[#F29A0C] to-[#E97818]",
                bgLight: "from-[#F29A0C]/10 to-transparent",
                borderCol: "rgba(242,154,12,0.3)"
              },
              {
                step: "Stage 03",
                badge: "Healing",
                title: "Manas Shanti",
                subtitle: "Therapeutic Restoration",
                desc: "Targeted endocrine and hormonal balance, deep fascia release, and somatic tension discharge through restorative Yoga Nidra.",
                icon: FaSpa,
                color: "#D97706",
                grad: "from-[#D97706] to-[#B45309]",
                bgLight: "from-[#D97706]/10 to-transparent",
                borderCol: "rgba(217,119,6,0.3)"
              },
              {
                step: "Stage 04",
                badge: "Mastery",
                title: "Brahma Vidya",
                subtitle: "Certification & Beyond",
                desc: "Mastery of advanced inversions, Vedic philosophy, Patanjali Sutras, and international Yoga Alliance teaching credentials.",
                icon: FaGraduationCap,
                color: "#C8660A",
                grad: "from-[#C8660A] to-[#9A3412]",
                bgLight: "from-[#C8660A]/10 to-transparent",
                borderCol: "rgba(200,102,10,0.3)"
              },
            ].map((stage, idx) => {
              const Icon = stage.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.12 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="rounded-[34px] bg-gradient-to-b from-white/95 via-white/90 to-[#FFF8EE]/95 border-2 border-[#F4D9B5] p-7 shadow-[0_20px_50px_rgba(242,154,12,0.08)] hover:shadow-[0_30px_70px_rgba(242,154,12,0.20)] hover:border-[#F29A0C] transition-all duration-300 relative overflow-hidden flex flex-col justify-between group backdrop-blur-xl"
                >
                  {/* Subtle top gradient accent banner */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${stage.grad}`} />
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${stage.bgLight} rounded-full blur-2xl pointer-events-none group-hover:scale-150 transition-transform duration-500`} />

                  <div className="space-y-4 relative z-10">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-widest px-3.5 py-1.5 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#F29A0C] shadow-sm">
                        {stage.step}
                      </span>
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFF8EE] to-[#FFF0D6] border border-[#F4D9B5] text-[#F29A0C] group-hover:bg-gradient-to-r group-hover:${stage.grad} group-hover:text-white flex items-center justify-center text-xl transition-all duration-300 shadow-md group-hover:rotate-6`}>
                        <Icon />
                      </div>
                    </div>

                    <div className="space-y-1 pt-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: stage.color }}>{stage.badge}</span>
                      <h3 className="text-xl font-bold text-[#111111] group-hover:text-[#F29A0C] transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {stage.title}
                      </h3>
                      <p className="text-xs font-semibold text-[#F29A0C]">{stage.subtitle}</p>
                    </div>

                    <p className="text-xs text-[#666666] leading-[1.75] font-normal">
                      {stage.desc}
                    </p>
                  </div>

                  <div className="pt-5 mt-5 border-t border-[#F4D9B5]/60 flex items-center justify-between text-xs font-bold text-[#111111] relative z-10">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#6B8A5A]" />
                      <span>Step {idx + 1} of 4</span>
                    </span>
                    <FiCheckCircle className="text-[#6B8A5A] text-lg" />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* ── 1. ACCREDITATION & TRUST HIGHLIGHTS ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 pt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -6, scale: 1.02 }}
              className="p-8 rounded-[34px] bg-gradient-to-br from-white/95 via-white/90 to-[#FFF8EE]/90 border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(242,154,12,0.08)] hover:shadow-[0_25px_60px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] flex flex-col items-center text-center space-y-3.5 backdrop-blur-2xl transition-all duration-300 relative overflow-hidden group"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6B8A5A]/20 to-[#6B8A5A]/5 border border-[#6B8A5A]/30 flex items-center justify-center text-[#6B8A5A] text-3xl shadow-sm group-hover:scale-110 transition-transform">
                <FaCertificate />
              </div>
              <h4 className="text-lg font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Yoga Alliance USA Registered
              </h4>
              <p className="text-xs text-[#666666] leading-relaxed">
                Globally recognized curriculum standards, professional training ethics, and verified certification credentials.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -6, scale: 1.02 }}
              className="p-8 rounded-[34px] bg-gradient-to-br from-white/95 via-white/90 to-[#FFF8EE]/90 border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(242,154,12,0.08)] hover:shadow-[0_25px_60px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] flex flex-col items-center text-center space-y-3.5 backdrop-blur-2xl transition-all duration-300 relative overflow-hidden group"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F29A0C]/20 to-[#F29A0C]/5 border border-[#F29A0C]/30 flex items-center justify-center text-[#F29A0C] text-3xl shadow-sm group-hover:scale-110 transition-transform">
                <FaAward />
              </div>
              <h4 className="text-lg font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Gold Medalist Mentorship
              </h4>
              <p className="text-xs text-[#666666] leading-relaxed">
                Direct teaching supervision, anatomical posture corrections, and breathing science curated by Mrs. Divya Nithin Bhojwani.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -6, scale: 1.02 }}
              className="p-8 rounded-[34px] bg-gradient-to-br from-white/95 via-white/90 to-[#FFF8EE]/90 border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(242,154,12,0.08)] hover:shadow-[0_25px_60px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] flex flex-col items-center text-center space-y-3.5 backdrop-blur-2xl transition-all duration-300 relative overflow-hidden group"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6B8A5A]/20 to-[#6B8A5A]/5 border border-[#6B8A5A]/30 flex items-center justify-center text-[#6B8A5A] text-3xl shadow-sm group-hover:scale-110 transition-transform">
                <FaGlobeAsia />
              </div>
              <h4 className="text-lg font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Global Multi-Device Sadhana
              </h4>
              <p className="text-xs text-[#666666] leading-relaxed">
                Synchronized live interactive classes and high-definition video libraries on Web, iOS, Android, and Tablets.
              </p>
            </motion.div>
          </div>

          {/* ── 2. FREQUENTLY ASKED QUESTIONS ACCORDION ── */}
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-2.5">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20 text-[#F29A0C] text-xs font-bold uppercase tracking-wider">
                <FaQuestionCircle />
                <span>Curriculum Insights</span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Frequently Asked Questions
              </h3>
              <p className="text-xs sm:text-sm text-[#666666] max-w-xl mx-auto">
                Everything you need to know about our pedagogical roadmap, live corrections, and certificates.
              </p>
            </div>

            <div className="space-y-4">
              {COURSE_FAQS.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                    className="rounded-[28px] border-2 border-[#F4D9B5] bg-gradient-to-r from-white/95 via-white/90 to-[#FFF8EE]/90 shadow-[0_12px_35px_rgba(242,154,12,0.05)] overflow-hidden transition-all duration-300 hover:border-[#F29A0C] backdrop-blur-xl"
                  >
                    <button
                      onClick={() => toggleFaq(idx)}
                      className="w-full px-6 sm:px-8 py-5 flex items-center justify-between text-left font-bold text-[#111111] text-sm sm:text-base focus:outline-none transition-colors duration-300 hover:text-[#F29A0C] cursor-pointer"
                    >
                      <span className="flex items-center gap-3.5 pr-4">
                        <GiLotus className="text-[#F29A0C] flex-shrink-0 text-xl" />
                        <span className="font-extrabold">{faq.question}</span>
                      </span>
                      <FaChevronDown
                        size={13}
                        className={`text-[#888888] flex-shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#F29A0C]" : ""}`}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 sm:px-8 pb-6 pt-1 text-[#444444] text-xs sm:text-sm leading-relaxed pl-12 sm:pl-14 border-t border-[#F4D9B5]/50 bg-[#FFFDFB]">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>

        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          5. GRAND LUXURY ASHRAM SANCTUARY CTA BANNER (BROWN THEME & ORANGE GLOW BORDER)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-12 relative overflow-hidden bg-gradient-to-b from-[#FFF9F2] via-[#FFF3DF] to-[#FFF9F2]">
        
        {/* Ambient Warm Golden & Amber Glows in Background */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[520px] rounded-full bg-gradient-to-r from-[#F29A0C]/25 via-[#FF8400]/20 to-[#6B8A5A]/20 blur-[130px] pointer-events-none" />

        <div className="max-w-[1440px] mx-auto relative z-10">
          <div className="relative">
            
            {/* Vivid Orange Glowing Aura Behind the Tilt Card Border */}
            <div className="absolute -inset-2.5 bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E97818] rounded-[48px] blur-2xl opacity-60 pointer-events-none animate-pulse" />
            <div className="absolute -inset-4 bg-gradient-to-r from-[#F29A0C]/40 via-[#FFA41B]/30 to-[#F29A0C]/40 rounded-[56px] blur-3xl opacity-70 pointer-events-none" />

            <LuxuryTiltCard maxTilt={4}>
              <div
                className="relative overflow-hidden rounded-[36px] sm:rounded-[44px] border-2 border-[#F29A0C] text-white p-6 sm:p-10 lg:p-14 shadow-[0_0_50px_rgba(242,154,12,0.45),0_30px_90px_rgba(0,0,0,0.6)] transition-shadow duration-500 group backdrop-blur-3xl"
                style={{
                  background: "linear-gradient(135deg, rgba(26, 12, 5, 0.96) 0%, rgba(44, 20, 8, 0.93) 50%, rgba(18, 8, 3, 0.98) 100%)",
                }}
              >
                
                {/* Background Sacred Mandala / Ashram Photographic Artwork Blend */}
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-65 mix-blend-screen scale-[1.02] transition-transform duration-1000 group-hover:scale-105 pointer-events-none"
                  style={{ backgroundImage: "url('/assets/trainers_bottom_bg.jpg')" }}
                />

                {/* Dark Brown Vignette to Ensure Absolute Crystal-Clear Readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#1A0C05]/95 via-[#2C1408]/75 to-[#1A0C05]/90 pointer-events-none" />

                {/* Animated Rotating Sacred Om / Mandala Watermark in Corner */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                  className="absolute -right-20 -bottom-20 w-[380px] h-[380px] opacity-15 text-[#FFD166] pointer-events-none"
                >
                  <FaOm className="w-full h-full" />
                </motion.div>

                {/* Top Golden-Orange Energy Shimmer Streak */}
                <div
                  className="absolute top-0 left-0 right-0 h-[4px]"
                  style={{
                    background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #FF8400, #F29A0C, transparent)",
                  }}
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center relative z-10">
                  
                  {/* Left Column: Heading, Description & Action Buttons (7 Columns) */}
                  <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
                    <div className="inline-flex items-center gap-2.5 rounded-full border border-[#F29A0C]/60 bg-black/60 px-4 py-2 backdrop-blur-xl shadow-[0_0_15px_rgba(242,154,12,0.3)]">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#F29A0C] animate-ping" />
                      <span className="text-[#FFD166] text-[11px] tracking-[2.5px] uppercase font-black">
                        DIVYYOG FOUNDATION · SACRED TRANSFORMATION
                      </span>
                    </div>

                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.15] tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      Awaken Your Body &<br />
                      <span className="bg-gradient-to-r from-[#FFD166] via-[#FFA41B] to-[#F29A0C] bg-clip-text text-transparent drop-shadow-[0_4px_30px_rgba(242,154,12,0.7)]">
                        Transform Your Life
                      </span>
                    </h2>

                    <p className="text-xs sm:text-sm md:text-base text-white/90 leading-relaxed max-w-xl mx-auto lg:mx-0 font-medium drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                      Join a global community of practitioners across 15+ countries. Experience personalized posture corrections, restorative breathwork, and transformational sadhana under world-class faculty.
                    </p>

                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                      <Link to={auth.loggedIn ? "/user/dashboard" : "/register"}>
                        <motion.button
                          whileHover={{ scale: 1.04, y: -2 }}
                          whileTap={{ scale: 0.96 }}
                          className="rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white px-8 sm:px-10 py-4 text-xs sm:text-sm font-black shadow-[0_15px_40px_rgba(242,154,12,0.65)] hover:shadow-[0_20px_50px_rgba(242,154,12,0.85)] transition-all cursor-pointer flex items-center gap-2.5 group"
                        >
                          <span>{auth.loggedIn ? "Go to Dashboard" : "Start Learning Journey"}</span>
                          <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                      </Link>

                      <Link to="/trainers">
                        <motion.button
                          whileHover={{ scale: 1.03, y: -2 }}
                          whileTap={{ scale: 0.96 }}
                          className="rounded-full border-2 border-[#F29A0C]/70 bg-black/50 backdrop-blur-xl text-white px-7 sm:px-8 py-4 text-xs sm:text-sm font-extrabold hover:border-[#FFD166] hover:bg-[#F29A0C]/20 transition-all cursor-pointer flex items-center gap-2 shadow-lg"
                        >
                          <FiCompass className="text-[#FFD166] text-base" />
                          <span>Meet Master Faculty</span>
                        </motion.button>
                      </Link>
                    </div>
                  </div>

                  {/* Right Column: Structured Luxury Highlight Cards (5 Columns) */}
                  <div className="lg:col-span-5 flex flex-col gap-4 w-full">
                    
                    {/* Highlight Card 1 - Lead Faculty */}
                    <motion.div
                      whileHover={{ scale: 1.02, y: -3 }}
                      className="rounded-[24px] border-2 border-[#F29A0C]/50 bg-[#241006]/95 backdrop-blur-2xl p-4.5 sm:p-5 shadow-[0_15px_40px_rgba(0,0,0,0.5)] hover:border-[#F29A0C] transition-all"
                    >
                      <div className="flex items-center gap-3.5">
                        <img
                          src="/assets/Divya_1.png"
                          alt="Divya Nithin Bhojwani"
                          className="w-14 h-14 min-w-[56px] min-h-[56px] rounded-2xl object-cover object-top border-2 border-[#F29A0C] shadow-md flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <h4 className="text-sm font-black text-white truncate">Mrs. Divya Nithin Bhojwani</h4>
                          <p className="text-[11px] text-[#FFD166] font-bold tracking-wide">Gold Medalist · Founder</p>
                          <p className="text-[11px] text-white/80 mt-1 leading-snug font-medium italic line-clamp-2">
                            "Authentic yogic alignment transforms both physical stability and inner stillness."
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Highlight Card 2 - Live Interactive Glowing Accent */}
                    <motion.div
                      whileHover={{ scale: 1.02, y: -3 }}
                      className="rounded-[24px] border-2 border-white/40 bg-gradient-to-br from-[#F29A0C] via-[#FF8400] to-[#E97818] p-4.5 sm:p-5 shadow-[0_20px_50px_rgba(242,154,12,0.55),0_0_25px_rgba(255,132,0,0.35)] text-white"
                    >
                      <div className="flex items-start gap-3.5">
                        <div className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-xl bg-white/20 backdrop-blur-xl flex items-center justify-center text-xl shadow-inner flex-shrink-0">
                          <GiMeditation className="text-white text-2xl" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-black">100% Live Interactive</h4>
                            <span className="px-2 py-0.5 rounded-full bg-white/25 text-[10px] font-extrabold uppercase tracking-wide">Live Camera</span>
                          </div>
                          <p className="text-[11px] text-white/95 mt-1 leading-snug font-medium">
                            Real-time camera posture corrections & personalized alignment feedback in every session.
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Highlight Card 3 - Yoga Alliance Credential */}
                    <motion.div
                      whileHover={{ scale: 1.02, y: -3 }}
                      className="rounded-[24px] border-2 border-[#F29A0C]/50 bg-[#241006]/95 backdrop-blur-2xl p-4.5 sm:p-5 shadow-[0_15px_40px_rgba(0,0,0,0.5)] hover:border-[#F29A0C] transition-all"
                    >
                      <div className="flex items-start gap-3.5">
                        <div className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-xl bg-[#6B8A5A]/30 border border-[#6B8A5A]/50 text-[#9DC08B] flex items-center justify-center text-xl font-black shadow-sm flex-shrink-0">
                          <FaCertificate />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-black text-white">Yoga Alliance USA</h4>
                            <span className="px-2 py-0.5 rounded-full bg-[#6B8A5A]/30 text-[#9DC08B] text-[10px] font-bold border border-[#6B8A5A]/40">RYT-200</span>
                          </div>
                          <p className="text-[11px] text-white/80 mt-1 leading-snug font-medium">
                            International credentials recognized by yoga institutions worldwide.
                          </p>
                        </div>
                      </div>
                    </motion.div>

                  </div>

                </div>
              </div>
            </LuxuryTiltCard>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          5. INTERACTIVE COURSE SYLLABUS & DOSSIER MODAL (ZERO PRICING • DEEP PEDAGOGY)
      ══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {selectedCourseModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedCourseModal(null)}
              className="fixed inset-0 bg-black/65 backdrop-blur-md"
            />

            {/* Modal Dialog Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-[36px] border border-[#F4D9B5] shadow-[0_30px_100px_rgba(0,0,0,0.35)] z-10 p-6 sm:p-10 text-[#111111]"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedCourseModal(null)}
                className="absolute top-6 right-6 w-11 h-11 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#111111] hover:text-[#F29A0C] hover:border-[#F29A0C] flex items-center justify-center text-lg transition-all cursor-pointer z-20"
                aria-label="Close modal"
              >
                <FiX />
              </button>

              <div className="space-y-8">
                
                {/* Header Course Details */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pt-2">
                  <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-3xl overflow-hidden border-2 border-[#F4D9B5] flex-shrink-0 shadow-md bg-[#201006]">
                    <img
                      src={selectedCourseModal.image}
                      alt={selectedCourseModal.title}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>

                  <div className="space-y-2 text-center sm:text-left flex-1">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F29A0C]/10 text-[#F29A0C] text-xs font-bold">
                      <GiLotus />
                      <span>{selectedCourseModal.level} • {selectedCourseModal.duration}</span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-[#111111] leading-tight tracking-[0.02em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {selectedCourseModal.title}
                    </h3>
                    <p className="text-sm font-semibold text-[#F29A0C]">
                      {selectedCourseModal.subtitle}
                    </p>
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 text-xs text-[#666666] pt-1">
                      <span className="flex items-center gap-1.5 font-bold text-[#111111]">
                        <img src={selectedCourseModal.instructorImg} alt="" className="w-5 h-5 rounded-full object-cover object-top border border-[#F29A0C]" />
                        Taught by {selectedCourseModal.instructor}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-[#6B8A5A]">
                        <FiStar className="text-[#F29A0C] fill-current" /> {selectedCourseModal.rating} ({selectedCourseModal.learners})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Course Overview */}
                <div className="space-y-3">
                  <h4 className="text-base font-bold text-[#111111] flex items-center gap-2 leading-snug">
                    <FiCompass className="text-[#F29A0C]" />
                    <span>What You Will Learn with the Trainer</span>
                  </h4>
                  <p className="text-sm text-[#555555] leading-relaxed">
                    {selectedCourseModal.overview}
                  </p>
                </div>

                {/* Detailed Techniques & Asanas Learned */}
                <div className="space-y-3">
                  <h4 className="text-base font-bold text-[#111111] flex items-center gap-2 leading-snug">
                    <FiAward className="text-[#6B8A5A]" />
                    <span>Techniques, Asanas & Breathwork Mastered</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedCourseModal.whatYouWillLearn.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3.5 rounded-2xl bg-[#FFF8EE]/60 border border-[#F4D9B5]/80 text-xs sm:text-sm text-[#222222]">
                        <FiCheckCircle className="text-[#6B8A5A] text-base flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Weekly Module Progression */}
                <div className="space-y-3">
                  <h4 className="text-base font-bold text-[#111111] flex items-center gap-2 leading-snug">
                    <FiBookOpen className="text-[#F29A0C]" />
                    <span>Curriculum Module Progression</span>
                  </h4>
                  <div className="space-y-2.5">
                    {selectedCourseModal.modules.map((mod, i) => (
                      <div key={i} className="p-4 rounded-2xl bg-[#FFF8EE]/40 border border-[#F4D9B5] space-y-1">
                        <div className="text-xs sm:text-sm font-bold text-[#111111]">{mod.title}</div>
                        <div className="text-xs text-[#666666] leading-relaxed">{mod.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Target Health & Wellness Benefits */}
                <div className="p-4 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] flex items-center gap-3 text-xs text-[#555555]">
                  <FiShield className="text-[#F29A0C] text-lg flex-shrink-0" />
                  <div>
                    <strong className="text-[#111111]">Target Health Outcomes: </strong>
                    {selectedCourseModal.targetBenefits}
                  </div>
                </div>

                {/* Modal Footer (Auth-Aware • Zero Pricing) */}
                <div className="pt-4 border-t border-[#F4D9B5] flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-[#777777]">
                    Curriculum guided by {selectedCourseModal.instructor} ({selectedCourseModal.instructorRole}).
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedCourseModal(null)}
                      className="px-6 py-2.5 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-xs font-bold text-[#111111] hover:bg-[#F4D9B5]/40 transition-colors cursor-pointer"
                    >
                      Close Syllabus
                    </button>
                    
                    {auth.loggedIn ? (
                      <Link
                        to="/user/dashboard"
                        onClick={() => setSelectedCourseModal(null)}
                        className="px-7 py-2.5 rounded-full bg-gradient-to-r from-[#6B8A5A] to-[#4B6B3A] text-xs font-bold text-white shadow-md hover:scale-105 transition-transform"
                      >
                        Access in Dashboard
                      </Link>
                    ) : (
                      <Link
                        to="/register"
                        onClick={() => setSelectedCourseModal(null)}
                        className="px-7 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-xs font-bold text-white shadow-md hover:scale-105 transition-transform"
                      >
                        Start Learning Journey
                      </Link>
                    )}
                  </div>
                </div>

              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
